import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ChildFormModal from "../../components/ChildFormModal";
import PhoneInput from "../../components/PhoneInput";
import { joinName } from "../../components/family/familyDisplay";
import {
  childrenService,
  familiesService,
  lookupsService,
} from "../../services/apiService";
import { usePermission } from "../../hooks/usePermission";
import { confirmAction } from "../../utils/confirm";
import { showToast } from "../../utils/toast";
import { validateForm } from "../../utils/validate";
import { formatPhone } from "../../utils/phone";

const EMPTY = {
  id: null,
  primary_first_name: "",
  primary_last_name: "",
  primary_relationship: "",
  email: "",
  phone: "",
  secondary_first_name: "",
  secondary_last_name: "",
  secondary_relationship: "",
  secondary_phone: "",
  secondary_email: "",
  centre_id: "",
  is_active: 1,
};

const TABS = [
  { id: "profile", label: "Personal details" },
  { id: "children", label: "Children" },
];

function CarerSectionHead({ title, badge }) {
  return (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <h3 className="bw-carer-section-title mb-0">{title}</h3>
      {badge && <span className="badge bg-warning text-dark">{badge}</span>}
    </div>
  );
}

export default function FamilyEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can, isGroupAdmin } = usePermission();
  const canChildAdd = can("children", "is_add");
  const canChildEdit = can("children", "is_update");
  const canChildDelete = can("children", "is_delete");

  const tab = searchParams.get("tab") === "children" ? "children" : "profile";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [family, setFamily] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [centres, setCentres] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [childrenLoading, setChildrenLoading] = useState(false);
  const [children, setChildren] = useState([]);
  const [childForm, setChildForm] = useState(null);

  const familyLabel = useMemo(
    () =>
      family
        ? joinName(family.primary_first_name, family.primary_last_name, family.full_name)
        : "Family",
    [family]
  );

  const loadFamily = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await familiesService.getById(id);
      if (!res?.success || !res?.data) {
        setError(res?.msg || "Family not found");
        setFamily(null);
        return;
      }
      const row = res.data;
      setFamily(row);
      setForm({
        id: row.id,
        primary_first_name: row.primary_first_name || "",
        primary_last_name: row.primary_last_name || "",
        primary_relationship: row.primary_relationship || "",
        email: row.email || "",
        phone: formatPhone(row.phone || ""),
        secondary_first_name: row.secondary_first_name || "",
        secondary_last_name: row.secondary_last_name || "",
        secondary_relationship: row.secondary_relationship || "",
        secondary_phone: formatPhone(row.secondary_phone || ""),
        secondary_email: row.secondary_email || "",
        centre_id: row.centre_id || "",
        is_active: row.is_active,
      });
    } catch (err) {
      setError(err.response?.data?.msg || err.message || "Failed to load family");
      setFamily(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadChildren = useCallback(async () => {
    if (!id) return;
    setChildrenLoading(true);
    try {
      const res = await childrenService.getAll({ family_id: id, limit: 100 });
      setChildren(res?.data?.list || []);
    } catch (err) {
      showToast("error", err.response?.data?.msg || "Could not load children");
      setChildren([]);
    } finally {
      setChildrenLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadFamily();
  }, [loadFamily]);

  useEffect(() => {
    lookupsService.getCentres().then((res) => setCentres(res?.data || []));
  }, []);

  useEffect(() => {
    if (tab === "children" && family) {
      loadChildren();
    }
  }, [tab, family, loadChildren]);

  const setTab = (nextTab) => {
    if (nextTab === "profile") {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: nextTab }, { replace: true });
    }
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !validateForm(e.currentTarget, [
        { name: "primary_first_name", value: form.primary_first_name, label: "First name", required: true },
        { name: "primary_last_name", value: form.primary_last_name, label: "Last name", required: true },
        { name: "email", value: form.email, label: "Email", required: true, type: "email" },
        { name: "phone", value: form.phone, label: "Mobile", required: true, type: "auMobile" },
        { name: "secondary_phone", value: form.secondary_phone, label: "Secondary mobile", type: "auMobile" },
      ])
    )
      return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        full_name: [form.primary_first_name, form.primary_last_name].filter(Boolean).join(" "),
        phone: formatPhone(form.phone),
        secondary_phone: form.secondary_phone?.trim()
          ? formatPhone(form.secondary_phone)
          : form.secondary_phone,
      };
      const res = await familiesService.update(payload);
      if (res?.success) {
        showToast("success", "Family updated");
        await loadFamily();
      } else {
        showToast("error", res?.msg || "Save failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteChild = async (child) => {
    const result = await confirmAction({
      title: `Delete ${child.first_name}'s profile?`,
      icon: "warning",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!result.isConfirmed) return;
    const res = await childrenService.delete(child.id);
    if (res?.success) {
      showToast("success", "Child deleted");
      await loadChildren();
      await loadFamily();
    } else {
      showToast("error", res?.msg || "Failed");
    }
  };

  const onChildSaved = async () => {
    await loadChildren();
    await loadFamily();
  };

  if (loading) {
    return (
      <div className="card mb-4">
        <div className="card-body text-center py-5 text-muted">Loading…</div>
      </div>
    );
  }

  if (error || !family) {
    return (
      <div className="card mb-4">
        <div className="card-body text-center py-5">
          <p className="text-danger mb-3">{error || "Family not found"}</p>
          <Link to="/families" className="btn btn-outline-secondary">
            Back to families
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-4">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <strong>Edit family — {familyLabel}</strong>
        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate("/families")}>
          <i className="cil-arrow-left me-1" />
          Back to list
        </button>
      </div>

      <div className="card-body">
        <ul className="nav nav-tabs mb-4">
          {TABS.map((t) => (
            <li className="nav-item" key={t.id}>
              <button
                type="button"
                className={`nav-link${tab === t.id ? " active" : ""}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {t.id === "children" && (family.children_count || children.length) > 0 && (
                  <span className="badge bg-secondary ms-2">{family.children_count || children.length}</span>
                )}
              </button>
            </li>
          ))}
        </ul>

        {tab === "profile" && (
          <form id="family-edit-form" onSubmit={handleSubmit} noValidate>
            <div className="card border mb-0">
              <div className="card-body">
                <CarerSectionHead title="Primary carer" badge="Main contact" />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">First name <span className="text-danger">*</span></label>
                    <input name="primary_first_name" className="form-control" value={form.primary_first_name} onChange={onChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Last name <span className="text-danger">*</span></label>
                    <input name="primary_last_name" className="form-control" value={form.primary_last_name} onChange={onChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Relationship to child</label>
                    <input name="primary_relationship" className="form-control" value={form.primary_relationship} onChange={onChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Mobile <span className="text-danger">*</span></label>
                    <PhoneInput name="phone" value={form.phone} onChange={onChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email <span className="text-danger">*</span></label>
                    <input name="email" type="email" className="form-control" value={form.email} onChange={onChange} required />
                  </div>
                </div>

                <hr className="my-4" />

                <CarerSectionHead title="Secondary carer" badge="Additional" />
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">First name</label>
                    <input name="secondary_first_name" className="form-control" value={form.secondary_first_name} onChange={onChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Last name</label>
                    <input name="secondary_last_name" className="form-control" value={form.secondary_last_name} onChange={onChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Relationship to child</label>
                    <input name="secondary_relationship" className="form-control" value={form.secondary_relationship} onChange={onChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Mobile</label>
                    <PhoneInput name="secondary_phone" value={form.secondary_phone} onChange={onChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input name="secondary_email" type="email" className="form-control" value={form.secondary_email} onChange={onChange} />
                  </div>
                </div>

                <hr className="my-4" />

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Centre</label>
                    <select name="centre_id" className="form-select" value={form.centre_id || ""} onChange={onChange} disabled={!isGroupAdmin}>
                      <option value="">— Select centre —</option>
                      {centres.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 d-flex align-items-end">
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        id="famActiveEdit"
                        className="form-check-input"
                        name="is_active"
                        checked={!!form.is_active}
                        onChange={onChange}
                      />
                      <label htmlFor="famActiveEdit" className="form-check-label ms-1">Active</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <button type="button" className="btn btn-outline-secondary" onClick={() => navigate("/families")}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        )}

        {tab === "children" && (
          <>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <p className="text-muted small mb-0">
                Children linked to this family account.
              </p>
              {canChildAdd && (
                <button type="button" className="btn btn-sm btn-primary" onClick={() => setChildForm({ family, child: null })}>
                  <i className="cil-plus me-1" />
                  Add child
                </button>
              )}
            </div>

            {childrenLoading && <p className="text-muted mb-0">Loading…</p>}

            {!childrenLoading && children.length === 0 && (
              <p className="text-muted mb-0">No children linked to this family yet.</p>
            )}

            {!childrenLoading && children.length > 0 && (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Child</th>
                      <th>Centre</th>
                      <th>Room / Program</th>
                      <th>Days</th>
                      <th>Status</th>
                      <th style={{ width: 140 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {children.map((c) => (
                      <tr key={c.id}>
                        <td className="fw-semibold">
                          {c.first_name} {c.last_name || ""}
                        </td>
                        <td>{c.centre || "—"}</td>
                        <td>
                          <div>{c.room || "—"}</div>
                          <div className="small text-muted">{c.program || ""}</div>
                        </td>
                        <td className="small">{c.days || "—"}</td>
                        <td>
                          <span className="badge bg-info text-dark">{c.enrolment_status}</span>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            {canChildEdit && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setChildForm({ family, child: c })}
                              >
                                Edit
                              </button>
                            )}
                            {canChildDelete && isGroupAdmin && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteChild(c)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {childForm && (
        <ChildFormModal
          open
          family={childForm.family}
          child={childForm.child}
          onClose={() => setChildForm(null)}
          onSaved={onChildSaved}
        />
      )}
    </div>
  );
}
